import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import SocialFab from "./components/SocialFab";

import Home from "./pages/Home";
import ListenLive from "./pages/ListenLive";
import Church from "./pages/Church";
import Giving from "./pages/Giving";
import Contact from "./pages/Contact";
import Testimonies from "./pages/Testimonies";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

/**
 * Instantly snaps the window to the very top whenever the route changes.
 * Uses behavior: "instant" so tab/route switches feel like a hard reset
 * rather than a smooth visual scroll.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Target element may not be mounted yet on first navigation — retry a
      // few frames before falling back to top.
      let attempts = 0;
      const maxAttempts = 25; // ~400ms at 60fps
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          try {
            window.scrollTo({ top, behavior: "instant" });
          } catch {
            window.scrollTo(0, top);
          }
          return;
        }
        if (attempts++ < maxAttempts) {
          requestAnimationFrame(tryScroll);
        } else {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
          } catch {
            window.scrollTo(0, 0);
          }
        }
      };
      requestAnimationFrame(tryScroll);
      return;
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <Header />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#1F2833",
              color: "#FFFFFF",
              border: "1px solid rgba(197,209,235,0.25)",
              borderRadius: "0",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Shell><Home /></Shell>} />
          <Route path="/listen" element={<Shell><ListenLive /></Shell>} />
          <Route path="/church" element={<Shell><Church /></Shell>} />
          <Route path="/media" element={<Navigate to="/church" replace />} />
          <Route path="/giving" element={<Shell><Giving /></Shell>} />
          <Route path="/contact" element={<Shell><Contact /></Shell>} />
          <Route path="/testimonies" element={<Shell><Testimonies /></Shell>} />
          <Route path="/admin/login" element={<Shell><AdminLogin /></Shell>} />
          <Route
            path="/admin"
            element={
              <Shell>
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="*"
            element={
              <Shell>
                <main className="bg-canvas min-h-[60vh] flex items-center justify-center text-[#C5D1EB]">
                  <div className="text-center">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                      404
                    </p>
                    <h1 className="mt-3 font-display text-4xl text-white">
                      Page not found
                    </h1>
                  </div>
                </main>
              </Shell>
            }
          />
        </Routes>
        <SocialFab />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
